import { useState, useCallback, useEffect } from "react";
import {
  type Message,
  isAssistantActionRequestMessage,
} from "../component/types";
import { create } from "mutative";
import usePrevious from "utils/hooks/usePrevious";
import type { AI_AGENT_EVENTS } from "ee/utils/analyticsUtilTypes";

interface UseMessageManagementProps {
  initialAssistantMessage?: string;
  initialAssistantSuggestions?: Array<{ label: string; value: string }>;
  logEvent: (event: AI_AGENT_EVENTS) => void;
}

export function useMessageManagement({
  initialAssistantMessage,
  initialAssistantSuggestions,
  logEvent,
}: UseMessageManagementProps) {
  const [messages, setMessages] = useState<Message[]>([]);
  const [prompt, setPrompt] = useState("");
  const previousInitialAssistantMessage = usePrevious(initialAssistantMessage);
  const previousInitialAssistantSuggestions = usePrevious(
    initialAssistantSuggestions,
  );

  const getInitialAssistantMessage = useCallback(
    (
      initialMessage?: string,
      promptSuggestions?: Array<{ label: string; value: string }>,
    ): Message[] => {
      return [
        {
          id: Math.random().toString(),
          role: "assistant",
          type: "text",
          content: initialMessage || "",
          promptSuggestions: promptSuggestions || [],
          citations: [],
          createdAt: new Date(),
        },
      ];
    },
    [],
  );

  // This handles the case where the initial assistant message or suggestions are changed.
  // Why we need this? All the messages are stored in the "messages" state.
  // When we were updating the initial assistant message or suggestions, the message state was not updated. Due to which,
  // the user was not able to see the updated initial assistant message and suggestions.
  useEffect(
    function handleUpdateOfInitialAssistantMessageOrSuggestions() {
      if (
        previousInitialAssistantMessage === undefined &&
        previousInitialAssistantSuggestions === undefined
      ) {
        return;
      }

      // we only need to do this when user hasn't interacted with the chat.
      if (messages.length > 1) {
        return;
      }

      if (
        previousInitialAssistantMessage !== initialAssistantMessage ||
        previousInitialAssistantSuggestions !== initialAssistantSuggestions
      ) {
        setMessages(
          getInitialAssistantMessage(
            initialAssistantMessage,
            initialAssistantSuggestions,
          ),
        );
      }
    },
    [
      messages,
      initialAssistantMessage,
      initialAssistantSuggestions,
      getInitialAssistantMessage,
      previousInitialAssistantMessage,
      previousInitialAssistantSuggestions,
    ],
  );

  const getErrorAssistantMessage = useCallback((message: string): Message => {
    return {
      id: Math.random().toString(),
      role: "assistant",
      type: "error",
      content: message || "",
      citations: [],
      createdAt: new Date(),
    };
  }, []);

  const appendMessage = useCallback((message: Message) => {
    setMessages((prev) => [...prev, message]);
  }, []);

  const handlePromptChange = useCallback((newPrompt: string) => {
    setPrompt(newPrompt);
  }, []);

  const approveToolCall = useCallback(
    (messageId: string, toolCallId: string) => {
      logEvent("TOOL_CALL_APPROVED");
      setMessages((prevMessages) =>
        create(prevMessages, (draft) => {
          const message = draft.find((message) => message.id === messageId);

          if (!message || !isAssistantActionRequestMessage(message)) return;

          const toolCall = message.content.find(
            (toolCall) => toolCall.id === toolCallId,
          );

          if (!toolCall || !toolCall.isApprovalRequired) return;

          toolCall.executionStatus = "pending";
        }),
      );
    },
    [logEvent],
  );

  const rejectToolCall = useCallback(
    (messageId: string, toolCallId: string) => {
      setMessages((prevMessages) =>
        create(prevMessages, (draft) => {
          const message = draft.find((message) => message.id === messageId);

          if (!message || !isAssistantActionRequestMessage(message)) return;

          const toolCall = message.content.find(
            (toolCall) => toolCall.id === toolCallId,
          );

          if (!toolCall) return;

          toolCall.executionStatus = "rejected";
          (toolCall as { error: string }).error =
            "User refused to execute the tool call";
        }),
      );
    },
    [],
  );

  return {
    messages,
    setMessages,
    prompt,
    setPrompt,
    getInitialAssistantMessage,
    getErrorAssistantMessage,
    appendMessage,
    handlePromptChange,
    approveToolCall,
    rejectToolCall,
  };
}
